import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Shield, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

// Genera/revoca el código que vincula una tablet de árbitro a ESTE partido concreto (ver
// deviceView.ts). Se usa tanto en "Partidos en directo" (partido ya empezado) como en "Partidos"
// (preparar un partido antes de que empiece) — por eso el queryKey a invalidar se recibe por
// fuera en vez de asumir siempre el mismo listado.
export function RefereeCodeSection({
  reservationId,
  refereeCode,
  queryKey,
}: {
  reservationId: number;
  refereeCode: string | null;
  queryKey: unknown[];
}) {
  const queryClient = useQueryClient();
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  const generate = useMutation({
    mutationFn: () => api.post<{ code: string }>(`/matches/live/${reservationId}/referee-code`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error: any) => toast.error(error?.response?.data?.error ?? "No se pudo generar el código"),
  });

  const revoke = useMutation({
    mutationFn: () => api.delete(`/matches/live/${reservationId}/referee-code`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setConfirmingRevoke(false);
    },
    onError: () => toast.error("No se pudo quitar el código"),
  });

  if (!refereeCode) {
    return (
      <Button className="w-full" onClick={() => generate.mutate()} disabled={generate.isPending}>
        <Shield className="h-4 w-4" />
        Generar código de árbitro
      </Button>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 rounded-xl bg-brand-50 px-3 py-2">
      <div>
        <p className="text-[10px] font-medium tracking-wide text-brand-600 uppercase">Código de árbitro</p>
        <p className="font-mono text-lg font-bold tracking-[0.2em] text-brand-700">{refereeCode}</p>
      </div>
      <Button variant="ghost" onClick={() => setConfirmingRevoke(true)} disabled={revoke.isPending} title="Quitar código">
        <X className="h-4 w-4" />
      </Button>

      {confirmingRevoke && (
        <ConfirmDialog
          open={confirmingRevoke}
          onOpenChange={setConfirmingRevoke}
          title="Quitar código de árbitro"
          description="¿Seguro que quieres quitar este código? La tablet que lo esté usando perderá el acceso al marcador de inmediato."
          tone="primary"
          confirmLabel="Quitar código"
          onConfirm={() => revoke.mutate()}
        />
      )}
    </div>
  );
}
